"use client";

import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1, delayChildren: 0.1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 100, damping: 15 },
    },
};

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    ArrowLeft, Package, Funnel, X, CurrencyDollar, ArrowDown, ArrowUp, Info,
} from "@phosphor-icons/react";
import { CaretDown } from "@phosphor-icons/react";
import { Loader2, RefreshCcw, ShieldAlert, Download } from "lucide-react";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { Link } from "react-router-dom";
import axios from "axios";
import { numberWithCommas } from "@/lib/formatCommas";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSelector } from "react-redux";

const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1/';

const MetricCard = ({ title, value, subtitle, icon: Icon, color = "blue", index = 0 }) => {
    const colorClasses = {
        blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
        green: "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
        orange: "bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400",
        red: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400",
        purple: "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400",
    };

    return (
        <motion.div variants={itemVariants} initial="hidden" animate="visible" custom={index}>
            <Card className="bg-white dark:bg-slate-900 border shadow-sm hover:shadow-lg transition-shadow duration-300 h-full">
                <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{title}</p>
                            <p className="text-2xl font-bold mt-1 truncate">{value}</p>
                            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
                        </div>
                        <div className={cn("p-2 rounded-lg shrink-0", colorClasses[color])}>
                            <Icon className="h-5 w-5" weight="duotone" />
                        </div>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};

export default function InventoryValuationReportPage() {
    const { user } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [totals, setTotals] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [canViewCost, setCanViewCost] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [basis, setBasis] = useState("cost"); // "cost" | "retail"

    const [category, setCategory] = useState("");
    const [categories, setCategories] = useState([]);
    const [brand, setBrand] = useState("");
    const [brands, setBrands] = useState([]);
    const [shopId, setShopId] = useState("");
    const [shops, setShops] = useState([]);

    const [dateRange, setDateRange] = useState("this_month");
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
    const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

    const currency = user?.shop?.currency || "GHS";

    const hasPermission = () => {
        if (!user?.role?.permissions) return false;
        const permissions = typeof user.role.permissions === 'string'
            ? JSON.parse(user.role.permissions)
            : user.role.permissions;
        return permissions.includes('ViewReports') || permissions.includes('Reports') || permissions.includes('Settings');
    };

    // The value basis falls back to retail when the user can't view cost.
    const effectiveBasis = canViewCost ? basis : "retail";
    const fmtMoney = (v) => `${currency} ${numberWithCommas((parseFloat(v) || 0).toFixed(2))}`;

    useEffect(() => {
        const load = async (path, setter) => {
            try {
                const res = await axios.get(`${baseUrl}${path}`, { withCredentials: true });
                if (res.data.success) setter(res.data.data || []);
            } catch (e) { console.error(`Failed to fetch ${path}`); }
        };
        load("shops", setShops);
        load("categories", setCategories);
        load("brands", setBrands);
    }, []);

    useEffect(() => {
        const now = new Date();
        switch (dateRange) {
            case "today":
                setStartDate(format(now, "yyyy-MM-dd"));
                setEndDate(format(now, "yyyy-MM-dd"));
                break;
            case "last_7_days":
                setStartDate(format(subDays(now, 7), "yyyy-MM-dd"));
                setEndDate(format(now, "yyyy-MM-dd"));
                break;
            case "last_30_days":
                setStartDate(format(subDays(now, 30), "yyyy-MM-dd"));
                setEndDate(format(now, "yyyy-MM-dd"));
                break;
            case "this_month":
                setStartDate(format(startOfMonth(now), "yyyy-MM-dd"));
                setEndDate(format(now, "yyyy-MM-dd"));
                break;
            case "last_month": {
                const lastMonth = subMonths(now, 1);
                setStartDate(format(startOfMonth(lastMonth), "yyyy-MM-dd"));
                setEndDate(format(endOfMonth(lastMonth), "yyyy-MM-dd"));
                break;
            }
            case "custom":
                break;
        }
    }, [dateRange]);

    const buildParams = (currentPage) => {
        const params = { startDate, endDate, limit, page: currentPage };
        if (shopId) params.shop = shopId;
        if (category) params.category = category;
        if (brand) params.brand = brand;
        return params;
    };

    const fetchData = async (currentPage) => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${baseUrl}analytics/inventory-valuation`, {
                params: buildParams(currentPage),
                withCredentials: true,
            });
            if (res.data.success) {
                const d = res.data.data || {};
                setProducts(d.products || []);
                setTotals(d.totals || null);
                setPagination(d.pagination || null);
                setCanViewCost(!!d.canViewCostPrice);
            }
        } catch (error) {
            console.error("Failed to fetch inventory valuation:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
        fetchData(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, shopId, category, brand, limit]);

    useEffect(() => {
        if (page !== 1) fetchData(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    // Value picked from each product row based on the active basis.
    const rowValue = (p, phase) =>
        effectiveBasis === "cost" ? (p[`${phase}Cost`] || 0) : (p[`${phase}Retail`] || 0);
    const totalValue = (phase) =>
        totals ? (effectiveBasis === "cost" ? totals[phase]?.cost : totals[phase]?.retail) || 0 : 0;

    const fetchAllForExport = async () => {
        try {
            const params = buildParams(1);
            params.limit = 99999;
            const res = await axios.get(`${baseUrl}analytics/inventory-valuation`, {
                params, withCredentials: true,
            });
            return res.data.success ? (res.data.data?.products || []) : products;
        } catch {
            return products;
        }
    };

    const buildExportRows = (data) =>
        data.map((p) => ({
            Product: p.productName,
            Category: p.categoryName,
            Barcode: p.barcode || "",
            "Opening Qty": p.openingQty,
            "Stock In": p.addedQty,
            "Stock Out": p.removedQty,
            "Closing Qty": p.closingQty,
            [`Closing Value (${effectiveBasis === "cost" ? "Cost" : "Retail"}, ${currency})`]:
                rowValue(p, "closing").toFixed(2),
        }));

    const exportCSV = async () => {
        const rows = buildExportRows(await fetchAllForExport());
        const headers = Object.keys(rows[0] || {});
        const csvLines = [
            headers.join(","),
            ...rows.map((r) => headers.map((h) => {
                const val = String(r[h] ?? "");
                return val.includes(",") ? `"${val}"` : val;
            }).join(",")),
        ];
        saveAs(new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8" }), `inventory-valuation_${startDate}_${endDate}.csv`);
    };

    const exportExcel = async () => {
        const rows = buildExportRows(await fetchAllForExport());
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Inventory Valuation");
        const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([buf], { type: "application/octet-stream" }), `inventory-valuation_${startDate}_${endDate}.xlsx`);
    };

    const exportPDF = async () => {
        const data = await fetchAllForExport();
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text("Inventory Valuation Report", 14, 18);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Period: ${format(new Date(startDate), "dd MMM yyyy")} – ${format(new Date(endDate), "dd MMM yyyy")}`, 14, 26);
        doc.text(`Basis: ${effectiveBasis === "cost" ? "Cost" : "Retail"}  |  Generated: ${format(new Date(), "dd MMM yyyy HH:mm")}`, 14, 32);
        autoTable(doc, {
            startY: 38,
            head: [["Product", "Category", "Open", "In", "Out", "Close", `Close Value (${currency})`]],
            body: data.map((p) => [
                p.productName, p.categoryName, p.openingQty, p.addedQty, p.removedQty, p.closingQty,
                rowValue(p, "closing").toFixed(2),
            ]),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [48, 35, 45] },
        });
        doc.save(`inventory-valuation_${startDate}_${endDate}.pdf`);
    };

    const showCount = pagination
        ? `Showing ${((pagination.page - 1) * pagination.limit) + 1}–${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total} products`
        : 'Loading...';

    return (
        <div className="mx-auto p-4 space-y-6">
            {!hasPermission() ? (
                <Card className="mx-auto max-w-md mt-20">
                    <CardContent className="pt-6 text-center">
                        <ShieldAlert className="h-12 w-12 mx-auto text-red-500 mb-4" />
                        <h2 className="text-xl font-bold mb-2">Access Denied</h2>
                        <p className="text-muted-foreground mb-4">You don't have permission to view reports.</p>
                        <Link to="/dashboard"><Button>Go to Dashboard</Button></Link>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Header */}
                    <motion.div
                        className="flex flex-wrap justify-between items-center gap-4"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                        <div className="flex items-center gap-4">
                            <Link to="/dashboard/reports">
                                <Button variant="outline" size="icon"><ArrowLeft className="h-4 w-4" /></Button>
                            </Link>
                            <div>
                                <h1 className="text-2xl font-bold">Inventory Valuation</h1>
                                <p className="text-sm text-muted-foreground">Opening, movement and closing stock value over a period</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {canViewCost && (
                                <Tabs value={basis} onValueChange={setBasis}>
                                    <TabsList>
                                        <TabsTrigger value="cost">Cost</TabsTrigger>
                                        <TabsTrigger value="retail">Retail</TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            )}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" disabled={isLoading || products.length === 0}>
                                        <Download className="h-4 w-4 mr-2" />Export
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={exportCSV}>Export as CSV</DropdownMenuItem>
                                    <DropdownMenuItem onClick={exportExcel}>Export as Excel</DropdownMenuItem>
                                    <DropdownMenuItem onClick={exportPDF}>Export as PDF</DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <Button variant="outline" onClick={() => fetchData(page)} disabled={isLoading}>
                                <RefreshCcw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />Refresh
                            </Button>
                        </div>
                    </motion.div>

                    {/* Filters */}
                    <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
                        <Card className="border">
                            <CollapsibleTrigger asChild>
                                <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50">
                                    <div className="flex items-center gap-2">
                                        <Funnel className="h-4 w-4" />
                                        <span className="font-medium">Filters</span>
                                        <Badge variant="outline" className="ml-2">
                                            {format(new Date(startDate), "MMM dd")} - {format(new Date(endDate), "MMM dd, yyyy")}
                                        </Badge>
                                    </div>
                                    {filtersOpen ? <X className="h-4 w-4" /> : <CaretDown className="h-4 w-4" />}
                                </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <div className="p-4 pt-0 border-t">
                                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                                        <div className="space-y-2">
                                            <Label>Date Range</Label>
                                            <Select value={dateRange} onValueChange={setDateRange}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="today">Today</SelectItem>
                                                    <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                                                    <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                                                    <SelectItem value="this_month">This Month</SelectItem>
                                                    <SelectItem value="last_month">Last Month</SelectItem>
                                                    <SelectItem value="custom">Custom Range</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {dateRange === "custom" && (
                                            <>
                                                <div className="space-y-2">
                                                    <Label>Start Date</Label>
                                                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>End Date</Label>
                                                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                                </div>
                                            </>
                                        )}

                                        <div className="space-y-2">
                                            <Label>Shop</Label>
                                            <Select value={shopId || "all"} onValueChange={(val) => setShopId(val === "all" ? "" : val)}>
                                                <SelectTrigger><SelectValue placeholder="All Shops" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">All Shops</SelectItem>
                                                    {shops.map(shop => (
                                                        <SelectItem key={shop.id} value={shop.id.toString()}>{shop.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Category</Label>
                                            <Select value={category || "all"} onValueChange={(val) => setCategory(val === "all" ? "" : val)}>
                                                <SelectTrigger><SelectValue placeholder="All Categories" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">All Categories</SelectItem>
                                                    {categories.map(cat => (
                                                        <SelectItem key={cat.id} value={cat.id.toString()}>{cat.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Brand</Label>
                                            <Select value={brand || "all"} onValueChange={(val) => setBrand(val === "all" ? "" : val)}>
                                                <SelectTrigger><SelectValue placeholder="All Brands" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">All Brands</SelectItem>
                                                    {brands.map(b => (
                                                        <SelectItem key={b.id} value={b.id.toString()}>{b.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>
                            </CollapsibleContent>
                        </Card>
                    </Collapsible>

                    <AnimatePresence mode="wait">
                        {isLoading ? (
                            <motion.div className="flex items-center justify-center h-64" key="loader"
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
                                    <Loader2 className="h-8 w-8 text-primary" />
                                </motion.div>
                            </motion.div>
                        ) : (
                            <motion.div key="content" variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
                                {/* Summary Cards */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <MetricCard
                                        title="Opening Value"
                                        value={fmtMoney(totalValue("opening"))}
                                        subtitle={`${numberWithCommas(totals?.opening?.qty || 0)} units at start`}
                                        icon={Package} color="blue" index={0}
                                    />
                                    <MetricCard
                                        title="Stock In"
                                        value={fmtMoney(totalValue("added"))}
                                        subtitle={`${numberWithCommas(totals?.added?.qty || 0)} units added`}
                                        icon={ArrowUp} color="green" index={1}
                                    />
                                    <MetricCard
                                        title="Stock Out"
                                        value={fmtMoney(totalValue("removed"))}
                                        subtitle={`${numberWithCommas(totals?.removed?.qty || 0)} units removed`}
                                        icon={ArrowDown} color="red" index={2}
                                    />
                                    <MetricCard
                                        title="Closing Value"
                                        value={fmtMoney(totalValue("closing"))}
                                        subtitle={`${numberWithCommas(totals?.closing?.qty || 0)} units on hand`}
                                        icon={CurrencyDollar} color="purple" index={3}
                                    />
                                </div>

                                {/* Caveat note */}
                                <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-md p-3">
                                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                                    <p>
                                        Quantities are reconstructed from the stock movement history. Values apply
                                        the product's <strong>current</strong> {effectiveBasis === "cost" ? "cost" : "retail"} price
                                        to historical quantities (prices are not versioned per batch). Products with no
                                        recorded history before the start date are treated as opening 0.
                                    </p>
                                </div>

                                {/* Table */}
                                <motion.div variants={itemVariants}>
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-base">Valuation by Product</CardTitle>
                                            <CardDescription>{showCount}</CardDescription>
                                        </CardHeader>
                                        <div className="p-0 overflow-x-auto">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Product</TableHead>
                                                        <TableHead>Category</TableHead>
                                                        <TableHead className="text-right">Opening</TableHead>
                                                        <TableHead className="text-right">In</TableHead>
                                                        <TableHead className="text-right">Out</TableHead>
                                                        <TableHead className="text-right">Closing</TableHead>
                                                        <TableHead className="text-right">Closing Value</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {products.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={7} className="text-center h-32 text-muted-foreground">
                                                                No stock movement found for this period.
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        products.map((p) => (
                                                            <TableRow key={p.productId}>
                                                                <TableCell className="font-medium">{p.productName}</TableCell>
                                                                <TableCell className="text-muted-foreground">{p.categoryName}</TableCell>
                                                                <TableCell className="text-right">{numberWithCommas(p.openingQty)}</TableCell>
                                                                <TableCell className="text-right text-green-600">
                                                                    {p.addedQty > 0 ? `+${numberWithCommas(p.addedQty)}` : "—"}
                                                                </TableCell>
                                                                <TableCell className="text-right text-red-600">
                                                                    {p.removedQty > 0 ? `-${numberWithCommas(p.removedQty)}` : "—"}
                                                                </TableCell>
                                                                <TableCell className="text-right font-medium">{numberWithCommas(p.closingQty)}</TableCell>
                                                                <TableCell className="text-right font-medium">{fmtMoney(rowValue(p, "closing"))}</TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>

                                        {pagination && pagination.total > 0 && (
                                            <div className="flex items-center justify-between p-4 border-t">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-muted-foreground">Rows per page</span>
                                                    <Select value={limit.toString()} onValueChange={(val) => setLimit(parseInt(val))}>
                                                        <SelectTrigger className="w-[70px] h-8 text-xs"><SelectValue /></SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="25">25</SelectItem>
                                                            <SelectItem value="50">50</SelectItem>
                                                            <SelectItem value="100">100</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || isLoading}>
                                                        Previous
                                                    </Button>
                                                    <span className="text-sm text-muted-foreground min-w-[100px] text-center">
                                                        Page {page} of {pagination.pages || 1}
                                                    </span>
                                                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= (pagination.pages || 1) || isLoading}>
                                                        Next
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </Card>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            )}
        </div>
    );
}
